import * as i0 from '@angular/core';
import { Directive, Host, Input, HostBinding, EventEmitter, Output, HostListener, NgModule, ElementRef, Injectable, Inject, InjectionToken, PLATFORM_ID } from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { fromEvent, combineLatest, NEVER, merge } from 'rxjs';
import { map, startWith, switchMap, delay } from 'rxjs/operators';

const selectorName = 'sdActive';
class ActiveDirective {
    constructor(host, renderer) {
        this.host = host;
        this.renderer = renderer;
        this.active = true;
    }
    ngAfterViewInit() {
        this.renderer.addClass(this.host.nativeElement, 'is-active');
        this.renderer.addClass(this.host.nativeElement, 'is-active-bar');
    }
}
ActiveDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: ActiveDirective, deps: [{ token: i0.ElementRef, host: true }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
ActiveDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.2.2", type: ActiveDirective, selector: "[sdActive]", inputs: { active: ["sdActive", "active"] }, exportAs: ["sdActive"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: ActiveDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: `[sdActive]`,
                    exportAs: 'sdActive',
                }]
        }], ctorParameters: function () {
        return [{ type: i0.ElementRef, decorators: [{
                        type: Host
                    }] }, { type: i0.Renderer2 }];
    }, propDecorators: { active: [{
                type: Input,
                args: [selectorName]
            }] } });

class ErrorDirective {
    constructor(changeDetectorRef) {
        this.changeDetectorRef = changeDetectorRef;
        this.standalone = false;
    }
    set component(component) {
        this._component = component;
        this.changeDetectorRef.markForCheck();
    }
    get classes() {
        return {
            'checkbox-group__error-message': this._component === 'checkbox-group',
            'radio-group__error-message': this._component === 'radio-group',
            'input__error-message': ['input', 'native-select'].includes(this._component),
            'input__error-message--stand-alone': ['input', 'native-select'].includes(this._component) && this.standalone,
            'rating__error-message': this._component === 'rating',
        };
    }
}
ErrorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: ErrorDirective, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive });
ErrorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.2.2", type: ErrorDirective, selector: "[sdError], sd-error", inputs: { standalone: "standalone" }, host: { properties: { "class": "this.classes" } }, exportAs: ["sdError"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: ErrorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sdError], sd-error',
                    exportAs: 'sdError',
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { standalone: [{
                type: Input
            }], classes: [{
                type: HostBinding,
                args: ['class']
            }] } });

class LabelDirective {
    constructor(
    /** @internal */
    hostRef, changeDetectorRef) {
        this.hostRef = hostRef;
        this.changeDetectorRef = changeDetectorRef;
    }
    set component(component) {
        this._component = component;
        this.changeDetectorRef.markForCheck();
    }
    set inputId(inputId) {
        this._inputId = inputId;
        this.changeDetectorRef.markForCheck();
    }
    get inputId() {
        return this._inputId;
    }
    get classes() {
        return {
            input__label: this._component === 'input',
            'native-select__label': this._component === 'native-select',
        };
    }
}
LabelDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: LabelDirective, deps: [{ token: i0.ElementRef, host: true }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive });
LabelDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.2.2", type: LabelDirective, selector: "[sdLabel]", host: { properties: { "attr.for": "this.inputId", "class": "this.classes" } }, exportAs: ["sdLabel"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: LabelDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sdLabel]',
                    exportAs: 'sdLabel',
                }]
        }], ctorParameters: function () {
        return [{ type: i0.ElementRef, decorators: [{
                        type: Host
                    }] }, { type: i0.ChangeDetectorRef }];
    }, propDecorators: { inputId: [{
                type: HostBinding,
                args: ['attr.for']
            }], classes: [{
                type: HostBinding,
                args: ['class']
            }] } });

class RequiredDirective {
    constructor() {
        this.staticClasses = true;
    }
}
RequiredDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: RequiredDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
RequiredDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.2.2", type: RequiredDirective, selector: "[sdRequired], sd-required", host: { properties: { "class.required--asterisk": "this.staticClasses" } }, exportAs: ["sdRequired"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: RequiredDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sdRequired], sd-required',
                    exportAs: 'sdRequired',
                }]
        }], propDecorators: { staticClasses: [{
                type: HostBinding,
                args: ['class.required--asterisk']
            }] } });

class InsideOutsideClickDirective {
    constructor(element) {
        this.element = element;
        this.clickType = new EventEmitter();
    }
    closeOutside($event) {
        const element = this.element.nativeElement;
        if (!(element === $event.target || element.contains($event.target))) {
            this.clickType.emit({
                type: 'outside',
                element: this.element.nativeElement,
            });
        }
        else {
            this.clickType.emit({
                type: 'inside',
                element: this.element.nativeElement,
            });
        }
    }
}
InsideOutsideClickDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: InsideOutsideClickDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
InsideOutsideClickDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.2.2", type: InsideOutsideClickDirective, selector: "[sdInsideOutsideClick]", outputs: { clickType: "clickType" }, host: { listeners: { "document:click": "closeOutside($event)" } }, exportAs: ["sdInsideOutsideClick"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: InsideOutsideClickDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sdInsideOutsideClick]',
                    exportAs: 'sdInsideOutsideClick',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { clickType: [{
                type: Output
            }], closeOutside: [{
                type: HostListener,
                args: ['document:click', ['$event']]
            }] } });

class SmartDesignCommonModule {
}
SmartDesignCommonModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: SmartDesignCommonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SmartDesignCommonModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: SmartDesignCommonModule, declarations: [ActiveDirective, ErrorDirective, LabelDirective, InsideOutsideClickDirective, RequiredDirective], imports: [CommonModule], exports: [ActiveDirective, ErrorDirective, LabelDirective, InsideOutsideClickDirective, RequiredDirective] });
SmartDesignCommonModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: SmartDesignCommonModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: SmartDesignCommonModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [ActiveDirective, ErrorDirective, LabelDirective, InsideOutsideClickDirective, RequiredDirective],
                    imports: [CommonModule],
                    exports: [ActiveDirective, ErrorDirective, LabelDirective, InsideOutsideClickDirective, RequiredDirective],
                }]
        }] });

/** @internal */
const smoothHeight = trigger('grow', [
    transition('void <=> *', []),
    transition('* <=> *', [style({ height: '{{startHeight}}px' }), animate('.3s ease')], {
        params: { startHeight: 0 },
    }),
]);

/** @internal */
var BreakPoint;
(function (BreakPoint) {
    BreakPoint[BreakPoint["DESKTOP"] = 500] = "DESKTOP";
    BreakPoint[BreakPoint["TABLET"] = 400] = "TABLET";
    BreakPoint[BreakPoint["EXTRA"] = 300] = "EXTRA";
    BreakPoint[BreakPoint["BIG"] = 200] = "BIG";
    BreakPoint[BreakPoint["SMALL"] = 100] = "SMALL";
})(BreakPoint || (BreakPoint = {}));
/** @internal */
const MEDIA_QUERIES = {
    [BreakPoint.DESKTOP]: 'screen and (min-width: 1024px)',
    [BreakPoint.TABLET]: 'screen and (min-width: 768px) and (max-width: 1023px)',
    [BreakPoint.EXTRA]: 'screen and (min-width: 426px) and (max-width: 767px)',
    [BreakPoint.BIG]: 'screen and (min-width: 360px) and (max-width: 425px)',
    [BreakPoint.SMALL]: 'screen and (max-width: 359px)',
};

/** @internal */
class OutsideClickListenerService {
    constructor(document) {
        this.document = document;
        this.windowClick$ = fromEvent(this.document.body, 'click').pipe(map(($event) => $event.target));
    }
    static contains(container, target) {
        if (!container || !target) {
            return false;
        }
        if (container === target) {
            return true;
        }
        if (!('contains' in container)) {
            return OutsideClickListenerService.contains(container, target.parentNode);
        }
        return container.contains(target);
    }
    /**
     * Creates an Observable emitting a value for every click event inside body.
     * Emits the click position inside or outside the element.
     *
     * @param element The Node to add an outside listener to
     */
    create(element) {
        const nativeElement = element instanceof ElementRef ? element.nativeElement : element;
        return this.windowClick$.pipe(map((target) => OutsideClickListenerService.contains(nativeElement, target)), map((clickWasInside) => (clickWasInside ? 'INSIDE' : 'OUTSIDE')));
    }
}
OutsideClickListenerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: OutsideClickListenerService, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
OutsideClickListenerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: OutsideClickListenerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: OutsideClickListenerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [DOCUMENT]
                    }] }];
    } });

const SD_FEATURE_TOGGLE = new InjectionToken('SD_FEATURE_TOGGLE');

/** @internal */
class ObjectCompareService {
    isEqual(x, y) {
        if (x === null || x === undefined || y === null || y === undefined) {
            return x === y;
        }
        // after this just checking type of one would be enough
        if (x.constructor !== y.constructor) {
            return false;
        }
        // if they are functions, they should exactly refer to same one (because of closures)
        if (x instanceof Function) {
            return x === y;
        }
        // if they are regexps, they should exactly refer to same one (it is hard to better equality check on current ES)
        if (x instanceof RegExp) {
            return x === y;
        }
        if (x === y || x.valueOf() === y.valueOf()) {
            return true;
        }
        if (Array.isArray(x) && x.length !== y.length) {
            return false;
        }
        // if they are dates, they must had equal valueOf
        if (x instanceof Date) {
            return false;
        }
        // if they are strictly equal, they both need to be object at least
        if (!(x instanceof Object)) {
            return false;
        }
        if (!(y instanceof Object)) {
            return false;
        }
        const that = this;
        // recursive object equality check
        const p = Object.keys(x);
        return (Object.keys(y).every((i) => {
            return p.indexOf(i) !== -1;
        }) &&
            p.every((i) => {
                return that.isEqual(x[i], y[i]);
            }));
    }
}
ObjectCompareService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: ObjectCompareService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ObjectCompareService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: ObjectCompareService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: ObjectCompareService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/** @internal */
class RippleService {
    constructor(platformId) {
        if (!RippleService.isRippleEnabled && isPlatformBrowser(platformId)) {
            import('@smartdesign/runtime/elements/Ripple').then((m) => {
                m.enable();
            });
            RippleService.isRippleEnabled = true;
        }
    }
}
RippleService.isRippleEnabled = false;
RippleService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: RippleService, deps: [{ token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable });
RippleService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: RippleService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.2", ngImport: i0, type: RippleService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [PLATFORM_ID]
                    }] }];
    } });

/** @internal */
function queryListContainsVariant(references, variant) {
    return mergeEventsInQueryList(references, 'variant$', combineLatest).pipe(map((variants) => variants.some((v) => v === variant)), delay(1));
}
/**
 * Takes an QueryList with objects containing an Observable in property `eventName` and merges these observables into one.
 *
 * @param references QueryList
 * @param eventName the property holding the Observables that should be merged
 * @param combinationOperator the function that should be used to merge the events. defaults to rxjs/merge
 * @internal
 */
function mergeEventsInQueryList(references, eventName, combinationOperator = (events) => merge(...events)) {
    return references.changes.pipe(startWith(references), map((refs) => refs.filter((ref) => eventName in ref)), map((refs) => refs.map((ref) => ref[eventName])), switchMap((events) => ((events === null || events === void 0 ? void 0 : events.length) ? combinationOperator(events) : NEVER)));
}

/** @internal */
class ValueAccessorBase {
    constructor() {
        this.changed = new Array();
        this.touched = new Array();
        this.disabled = false;
    }
    get value() {
        return this.innerValue;
    }
    set value(value) {
        if (this.innerValue !== value) {
            this.innerValue = value;
            this.changed.forEach((f) => f(value));
        }
    }
    writeValue(value) {
        this.innerValue = value;
    }
    registerOnChange(fn) {
        this.changed.push(fn);
    }
    registerOnTouched(fn) {
        this.touched.push(fn);
    }
    touch() {
        this.touched.forEach((f) => f());
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { ActiveDirective, BreakPoint, ErrorDirective, InsideOutsideClickDirective, LabelDirective, MEDIA_QUERIES, ObjectCompareService, OutsideClickListenerService, RequiredDirective, RippleService, SD_FEATURE_TOGGLE, SmartDesignCommonModule, ValueAccessorBase, mergeEventsInQueryList, queryListContainsVariant, smoothHeight };
